import { useState } from "react"
import { FormModal, LoadingErrorModal } from "./form-modal"
import { FromDropdown } from "../input/form-dropdown"
import { useSelector } from "react-redux"
import { RootState } from "../../../store/configure-store"
import { httpService } from "../../../services/http-service"
import { useQuery as reactUseQuery } from "@tanstack/react-query"
import { AddSystemModal } from "./add-system-modal"
import { SystemDetails } from './add-system-modal'
import { FormInputText } from "../input/form-input-text"
import { SystemTypes } from "../is-required-system"
import { createSystemMutation } from "../../util-cmps/graphql-mutations/create-system"

export interface EntitySystemDetails {
    systemId: number | null
    integrationType: string
    isRequiredDaily: boolean
}
interface AddEntitySystemModalProps {
    entityName: string
    systemType: SystemTypes
    onAddEntitySystem: (systemType: SystemTypes, systemDetails: EntitySystemDetails) => void
    onCloseModal: () => void
    triggerMissingRequiredFields: () => void
    triggerErrorMsg: (txt: string) => void
}

interface ModalDetailsRes {
    systems: { id: number, name: string }[]
    integrationTypes: { id: string }[]
}

export const AddEntitySystemModal: React.FC<AddEntitySystemModalProps> = (
    { entityName, systemType, onAddEntitySystem, onCloseModal, triggerMissingRequiredFields, triggerErrorMsg }
) => {
    const companyName: any = useSelector((stateModule: RootState) => stateModule.companyName)!

    const [modalDetails, setModalDetails] = useState<ModalDetailsRes>()
    const [entitySystemDetails, setEntitySystemDetails] = useState<EntitySystemDetails>({ systemId: null, integrationType: '', isRequiredDaily: true })

    const [isShowSpinner, setIsShowSpinner] = useState(false)
    const [isAddSystemModalOpen, setIsSystemModalOpen] = useState(false)

    const fetchModalDetails = async () => {
        const query = `{
            systems {
                id
                name
            }
            integrationTypes {
                id
            }
        }
        `

        const modalDetails: ModalDetailsRes = await httpService.graphql(companyName.value, query)
        setModalDetails(modalDetails)
        return modalDetails
    }

    const { isLoading, isRefetching, isError } = reactUseQuery({
        queryKey: ['modal-details'],
        queryFn: fetchModalDetails
    })

    const onChangeFormValue = (event: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>) => {
        const { name, value } = event.target
        let updatedValue: null | string | number | boolean = value
        if (value === '') updatedValue = null
        else if (name === 'isRequiredDaily') updatedValue = value === 'true'
        setEntitySystemDetails({ ...entitySystemDetails, [name]: updatedValue })
    }

    const onAdd = async () => {
        if (!entitySystemDetails.systemId || !entitySystemDetails.integrationType) {
            return triggerMissingRequiredFields()
        }

        setIsShowSpinner(true)
        try {
            await onAddEntitySystem(systemType, entitySystemDetails)
        } catch {
            triggerErrorMsg('Failed creating entity system')
        } finally {
            setIsShowSpinner(false)
        }
    }

    const onAddSystem = async (systemDetails: SystemDetails) => {
        const {name, abbreviation, type, logoUrl} = systemDetails
        const query = createSystemMutation(name, abbreviation, type, logoUrl)
        const res = await httpService.graphql(companyName.value, query)
        const newSystem = res.createSystem.system

        const updatedModalDetails = {
            ...modalDetails,
            systems: [...modalDetails!.systems, { id: newSystem.id, name: newSystem.name }].sort((a, b) => a.name.localeCompare(b.name))
        }
        setModalDetails(updatedModalDetails as ModalDetailsRes)  /* FIX -  */
        setEntitySystemDetails({ ...entitySystemDetails, systemId: newSystem.id })
        setIsSystemModalOpen(false)

    }

    const modalHeader = 'Create Entity System'

    if (isError) return <LoadingErrorModal
        header={modalHeader}
        onCloseModal={onCloseModal}
        isError={isError}
    />

    if (isLoading || isRefetching) return <LoadingErrorModal
        header={modalHeader}
        onCloseModal={onCloseModal}
    />

    return <>
        <FormModal
            header={modalHeader}
            onCloseModal={onCloseModal}
            actionBtnText="Create"
            onActionBtn={onAdd}
            isShowSpinner={isShowSpinner}
        >
            <div className="form-section-card">
                <div className="field">
                    <FormInputText
                        label="Entity"
                        keyName=""
                        value={entityName}
                        onChange={onChangeFormValue}
                        isDisabled={true}
                    />
                </div>
                <div className="field-with-add">
                    <FromDropdown
                        keyName="systemId"
                        label="System"
                        options={modalDetails!.systems}
                        value={entitySystemDetails.systemId || ''}
                        onSelectValue={onChangeFormValue}
                        onAddBtn={() => setIsSystemModalOpen(true)}
                    />
                </div>
                <div className="field">
                    <FromDropdown
                        keyName="integrationType"
                        label="Integration type"
                        options={modalDetails!.integrationTypes.map((integrationType) => ({ id: integrationType.id, name: integrationType.id }))}
                        value={entitySystemDetails.integrationType}
                        onSelectValue={onChangeFormValue}
                    />
                </div>
                <div className="field">
                    <FromDropdown
                        keyName="isRequiredDaily"
                        label="Is required daily"
                        options={[true, false].map((bool) => ({ id: bool.toString(), name: bool.toString() }))}
                        value={entitySystemDetails.isRequiredDaily.toString()}
                        onSelectValue={onChangeFormValue}
                    />
                </div>
            </div>
        </FormModal>

        {isAddSystemModalOpen && <AddSystemModal
            onAddSystem={onAddSystem}
            onCloseModal={() => setIsSystemModalOpen(false)}
            triggerMissingRequiredFields={triggerMissingRequiredFields}
            triggerErrorMsg={triggerErrorMsg}
        />}
    </>
}